import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDaysToBusinessDate,
  type BillDetailDto,
  type BillFilterOptions,
  type BillKind,
  type BillListResponse,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Bills data access (SPEC §5.6).
 *
 * The From/To values are venue-local wall clock strings from
 * `<input type="datetime-local">`; they are converted to instants here and
 * resolved to BUSINESS DATES server-side. Nothing in the client decides which
 * day a bill belongs to — a 02:00 bill belonging to the previous business day
 * is a rule the server owns, so every client agrees with the reports.
 */

export interface BillFilters {
  kind: BillKind;
  search: string;
  deviceCategoryId: string | null;
  employeeId: string | null;
  /** `YYYY-MM-DDTHH:mm`, venue-local, as the picker produces it. */
  from: string;
  to: string;
  /**
   * The number off a customer's receipt (owner, 2026-09-23). When set it is a
   * LOOKUP: the server ignores the dates, the kind tab and every other filter
   * and answers with every bill that receipt carries.
   */
  receiptNumber?: string;
}

/**
 * The default window is the business day, written the way the venue reads it:
 * Opening Time on the business date, through Closing Time the next morning
 * (SPEC §5.7 — "25/7 09:00 -> 26/7 08:00").
 */
export function defaultRange(
  options: Pick<BillFilterOptions, 'currentBusinessDate' | 'openingTime' | 'closingTime'>,
) {
  return {
    from: `${options.currentBusinessDate}T${options.openingTime.slice(0, 5)}`,
    to: `${addDaysToBusinessDate(options.currentBusinessDate, 1)}T${options.closingTime.slice(0, 5)}`,
  };
}

/** Wall clock from the picker -> an instant the server can resolve. */
function toInstant(localValue: string): string | null {
  if (!localValue) return null;
  const parsed = new Date(localValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/**
 * The receipt number a search looks like, reduced as the server reduces it,
 * or null. Staff read the number off the paper into whichever box is nearest,
 * so a bare number in the ordinary search earns an offer to look the receipt
 * up across every date — that box only searches the window on screen. Same
 * shape the API matches (`bills.service`): digits, an optional `#`.
 */
export function receiptNumberLike(search: string): string | null {
  const typed = search.trim();
  if (!/^#?\s*\d{1,12}$/.test(typed)) return null;
  return typed.replace(/^#\s*/, '').replace(/^0+(?=\d)/, '');
}

export function billsQueryString(filters: BillFilters, page: number, pageSize: number): string {
  const params = new URLSearchParams();
  params.set('kind', filters.kind);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.deviceCategoryId) params.set('deviceCategoryId', filters.deviceCategoryId);
  if (filters.employeeId) params.set('employeeId', filters.employeeId);
  const from = toInstant(filters.from);
  const to = toInstant(filters.to);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (filters.receiptNumber?.trim()) params.set('receiptNumber', filters.receiptNumber.trim());
  return `?${params.toString()}`;
}

export function useBills(filters: BillFilters | null, page: number, pageSize: number) {
  const [data, setData] = useState<BillListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!filters) return;
    const id = ++requestId.current;
    setLoading(true);
    try {
      const response = await api.get<BillListResponse>(
        `/bills${billsQueryString(filters, page, pageSize)}`,
      );
      // Filters change fast while typing; a slow earlier response must not
      // overwrite the newer one it lost the race to.
      if (id === requestId.current) {
        setData(response);
        setError(null);
      }
    } catch (caught) {
      if (id === requestId.current) {
        setError(caught instanceof ApiError ? caught.message : 'Could not load bills.');
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}

/** Device Section and "All Users" dropdown contents, plus the venue's day boundary. */
export function useBillFilterOptions() {
  const [options, setOptions] = useState<BillFilterOptions | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<BillFilterOptions>('/bills/filter-options');
        if (!cancelled) setOptions(response);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not load filters.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, error };
}

export function useBillDetail(billId: string | null) {
  const [detail, setDetail] = useState<BillDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!billId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const response = await api.get<BillDetailDto>(`/bills/${billId}`);
        if (!cancelled) {
          setDetail(response);
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not load the bill.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billId]);

  return { detail, loading, error };
}
